import { SitecoreFetch } from 'Api/api/SitecoreApi';
import React, { useEffect, useState } from 'react';
import { HttpStatusCodes, IServerResponse } from 'Utilities/ClientResponseUtility';
import { CircularProgressSpinner } from '@lmig/sales-ui-circular-progress-spinner';
import { DataTable, Table, TableBody, TableCell, TableHeader, TableRow } from '@lmig/lmds-react-table';
import RichText from 'Feature/PageContent/Shared/Tiles/RichText';
import { Tooltip } from '@lmig/lmds-react-tooltip';
import { Pagination } from '@lmig/lmds-react-pagination';
import './QuotedNotSold.scss';
import {
  IconHome,
  IconAuto,
  IconBroken,
} from '@lmig/lmds-react-icons';
import { Link } from '@lmig/lmds-react-link';
import { getClientList, getClientListDeepLink } from 'Foundation/Configurations/GlobalConfiguration';
import { useLocalStorage } from 'Utilities/UseStorage';
import { IUserData } from 'Foundation/User/UserContextModels';
import { useUserData } from 'Foundation/User/UserContext';
import { Heading } from '@lmig/lmds-react-typography';
import { ErrorBoundary } from 'react-error-boundary';

interface IQuotedNotSoldDataProps {
  qnsNoDataRichText?: string | null;
  setHideQNSFooterDate?: (hideQNSFooterDate: boolean) => void | null;
}

interface IQuotedNotSoldDataRecord {
  policyGuid: string; //"home-guid-mno345",
  customerName: string; //"Wilma Bradley",
  totalPremium: number; //1720.80,
  effectiveDate: Date; //"2025-12-16",
  expirationDate: Date; //"2026-12-16",
  createdDateTime: Date; //"2024-01-16T13:30:00Z",
  lastModifiedDateTime: Date; //"2024-01-21T11:45:00Z",
  status: string; //"QUOTE",
  lineOfBusiness: string; //"HOME",
  ltvScore: number; //79.8,
  showInstantIncentive: boolean; //true
}

const GetPolicyIcon = ({ lineOfBusiness }: { lineOfBusiness: string }) => {
  const iconMap: { [key: string]: any } = {
    home: <IconHome size="16" />,
    auto: <IconAuto size="16" />
  };
  return iconMap[lineOfBusiness.toLowerCase()] ?? <></>;
};

const minutesUntilNextEastern530 = (): number => {
  const now = new Date();
  const easternNow = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  })
    .formatToParts(now)
    .reduce<Record<string, number>>((acc, part) => {
      if (part.type !== 'literal') acc[part.type] = Number(part.value);
      return acc;
    }, {});

  const { year, month, day } = easternNow;
  const dayToUse = (easternNow.hour <= 5 && easternNow.minute <= 30) ? day : day + 1;
  const easternTarget = Date.UTC(year, month - 1, dayToUse, 10, 30, 0);
  const offset = easternTarget - now.getTime();
  return Math.max(Math.ceil(offset / 60000), 0);
};

function craftStorageKey(userData: IUserData | undefined) {
  const qnsKey = 'QuotedNotSoldData';
  if (userData?.selectedStatCodeData?.SubStat && userData?.dpecid) {
    return `${userData.isMockingAgent ? 'mocking_' : ''}${qnsKey}_${userData.dpecid}_${userData?.selectedStatCodeData?.SubStat}`;
  } else {
    return `${qnsKey}_PLACEHOLDER`;
  }
};

const QuotedNotSoldData: React.FunctionComponent<IQuotedNotSoldDataProps> = (props) => {
  const resultsPerPage = 5;
  // const maxResults = 50;
  const [isLoading, setIsLoading] = useState(true);
  const [isServerError, setIsServerError] = useState(false);
  const [page, setPage] = useState(0);

  const expirationMinutes = minutesUntilNextEastern530();
  const { userData } = useUserData();
  const localStorageKey = craftStorageKey(userData);
  const [quotedNotSoldData, setQuotedNotSoldData] = useLocalStorage<{
    data?: IQuotedNotSoldDataRecord[] | null;
    isLoaded: boolean;
  }>(localStorageKey, 
    { isLoaded: false },
    expirationMinutes,);
  const [responseStatus, setResponseStatus] = useState<{ statusCode: number; message: string }>({
    statusCode: HttpStatusCodes.OK,
    message: '',
  });

  const LMDSPagination = () => {
    const itemCount = quotedNotSoldData?.data?.length ?? 0;
    const changeHandler = (e: any) => {
      setPage(e.page);
    };

    return itemCount > resultsPerPage ? (
      <Pagination
        id="qns-pagination"
        className="qns-pagination"
        withWords={false}
        itemCount={itemCount}
        onChange={changeHandler}
        showItemsPerPage={false}
        page={page}
        itemsPerPage={resultsPerPage}
      />
    ) : (
      <></>
    );
  };

  const handleHideQNSFooterDate = () => {
    if (props?.setHideQNSFooterDate !== null && props.setHideQNSFooterDate !== undefined) {
      props.setHideQNSFooterDate(true);
    }
  }

  useEffect(() => {
    if (!isLoading) return;
    if (quotedNotSoldData?.isLoaded) {
      setIsLoading(false);
      if (quotedNotSoldData?.data === null || quotedNotSoldData?.data === undefined || quotedNotSoldData?.data.length === 0) {
        handleHideQNSFooterDate();
      }
      return;
    }
    
    const fetchData = async () => {
      setQuotedNotSoldData({ data: {isLoaded: false} });
      let response = (await SitecoreFetch(`/api/sitecore/QuotedNotSoldAsync/GetQuotedNotSoldData`)) as IServerResponse;
      setResponseStatus({ statusCode: response.Status.Code, message: response.Status.Message });
      let result: IQuotedNotSoldDataRecord[] | null;
      if (response && response.Status?.Code === HttpStatusCodes.OK) {
        result = response?.Data?.quotes as IQuotedNotSoldDataRecord[];
        setQuotedNotSoldData({ data: {data: result, isLoaded: true} });
      } else {
        result = null;
        setIsServerError(true);
        handleHideQNSFooterDate();
      }
      setIsLoading(false);
      if (result === null || result.length === 0) {
        handleHideQNSFooterDate();
      }
    };

    try {
      fetchData();
    } catch (error) {
      console.error(error);
      setIsServerError(true);
      setIsLoading(false);
      handleHideQNSFooterDate();
    }
  }, []);

  useEffect(() => {
    if (!isLoading && (quotedNotSoldData?.data === null || quotedNotSoldData?.data?.length === 0)) {
      if (props?.setHideQNSFooterDate !== null && props.setHideQNSFooterDate !== undefined) {
        props.setHideQNSFooterDate(true);
      }
    }
  }, [isLoading, quotedNotSoldData]);

  const RenderLoading = () => {
    return (
      <CircularProgressSpinner
        className="qns-loading-spinner"
        size="32"
        aria-label="Loading data..."
      />
    );
  };

  const RenderLoadedData = () => {
    return (
      <div className="design-system-data-table-container">
        <div className="qns-table-container">
          <div className="design-system-data-table">
            <DataTable
              data-cs-mask // IMPORTANT: DO NOT REMOVE data-cs-mask attribute, this ensures the PII data is masked correctly for session replay
            > 
              <Table className="design-system-table" spacing="micro">
                <TableHeader className="qns-table-header">
                <TableRow>
                  <TableCell type="colHead" textAlign="left" className="qns-table-header-cell small-column-header qns-isi-cell">
                    {/* intentionally blank - instant incentives column */}
                  </TableCell>
                  <TableCell type="colHead" textAlign="left" className="qns-table-header-cell small-column-header qns-customer-name-cell">
                    Name
                  </TableCell>
                  <TableCell type="colHead" textAlign="left" className="qns-table-header-cell small-column-header qns-effective-cell">
                    Effective
                  </TableCell>
                  <TableCell type="colHead" textAlign="left" className="qns-table-header-cell small-column-header qns-premium-cell">
                    Premium
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody id="qns-table-body">
                {quotedNotSoldData?.data
                  ?.slice(
                    page * resultsPerPage,
                    Math.min(page * resultsPerPage + resultsPerPage, quotedNotSoldData.data?.length),
                  )
                  ?.map((data: IQuotedNotSoldDataRecord, rowIndex: any) => (
                    <TableRow key={`${rowIndex}-qns`}>
                      <TableCell key={`${rowIndex}-qns-instant-incentives`} className='qns-isi-cell'>
                        {data.showInstantIncentive
                          ? <div className="qns-tooltip">
                              <Tooltip
                                className='qns-tooltip-content'
                                content="This policy is eligible for an Instant Incentive bonus reward."
                                direction="right"
                              >
                                <div className="isi-icon" aria-label="Instant Incentives Eligible"/>
                              </Tooltip>
                            </div>
                          : <></>}
                      </TableCell>
                      <TableCell key={`${rowIndex}-qns-customer-name`}>
                        <div className="qns-customer-name-cell">
                          <div className="policy-icon">
                            <GetPolicyIcon lineOfBusiness={data.lineOfBusiness} />
                          </div>
                          <div className="customer-name">
                            <Link target="_blank" href={`${getClientListDeepLink()}?p=${data.policyGuid}`}>
                              {data.customerName}
                            </Link>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell key={`${rowIndex}-qns-effective-date`} className="qns-table-effective-date">
                          {data.effectiveDate
                            ? new Intl.DateTimeFormat('en-US', {
                              timeZone: 'UTC',
                              year: '2-digit',
                              month: '2-digit',
                              day: '2-digit'
                            }).format(new Date(data.effectiveDate))
                            : ''}
                      </TableCell>
                      <TableCell key={`${rowIndex}-qns-renewal-change`}>
                        <div className="renewal-change-container">
                          <span className="renewal-change-amount">
                            {data?.totalPremium?.toLocaleString('en-US', {style: 'currency', currency: 'USD',})}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </DataTable>
          </div>
        </div>
        <LMDSPagination />
      </div>
    )
  };

  const RenderNoDataRichText = () => {
    return (
      <RichText className="two-tab-tile-tab-1-richtext" content={props.qnsNoDataRichText ?? ''} />
    )
  }

  const RenderOnError = () => {
    return (
      <ErrorBoundary fallback={<div style={{ display: 'none' }}>{`ERROR in: QuotedNotSoldData: renderOnError`}</div>}>
        <div className="qns-error">
          <IconBroken size="48" />
          <Heading type="h4-light" className="qns-error-message-heading">Data currently unavailable</Heading>
          <span className="qns-error-message">Please try again later.</span>
          <Link className="qns-error-link" href={getClientList()} variant="standalone" target="_blank">
            See all quotes
          </Link>
        </div>
      </ErrorBoundary>
    )
  }

  return (
    <ErrorBoundary fallback={<RenderOnError/>}>
      {isLoading
        ? <RenderLoading />
        : quotedNotSoldData?.data?.length && quotedNotSoldData.data.length > 0 
          ? <RenderLoadedData />
          : (responseStatus?.statusCode === HttpStatusCodes.OK || !isServerError) && (props?.qnsNoDataRichText?.length !== undefined && props?.qnsNoDataRichText?.length > 0)
            ? <RenderNoDataRichText />
            : <RenderOnError />
      }
    </ErrorBoundary>
  );
};

export default QuotedNotSoldData;
